/**
 * Session and cookie storage management
 * Unified interface for managing session storage and cookies with automatic expiry
 */

import Cookies from 'js-cookie'
import { generateID, globalState } from './utils'
import type {
  StorageItem,
  SessionKeyDefinitions,
  StorageItemValue
} from './session.types'

/**
 * Base class for managing values in both SessionStorage and Cookies
 */
class StorageValue implements StorageItem {
  protected key: string
  protected expires: number
  protected aliasKey?: string

  /**
   * @param aliasKey — legacy `ss*` cookie name to mirror writes to. The Shopify
   * Web Pixel is still on `searchspring/shopify/pixel/*`, which reads these
   * names; if the theme stops writing them the pixel mints its own IDs and
   * theme/pixel attribution diverges. Do not remove while that pixel is live.
   */
  constructor(key: string, expires = 0, aliasKey?: string) {
    this.key = key
    this.expires = expires
    this.aliasKey = aliasKey
  }

  get(delimiter = ''): string | string[] | null {
    // Try to get value from Cookie first, then SessionStorage
    let value = Cookies.get(this.key) || sessionStorage.getItem(this.key)

    if (!value && this.aliasKey) {
      const legacyValue = Cookies.get(this.aliasKey)
      if (legacyValue) {
        this.set(legacyValue)
        value = legacyValue
      }
    }

    if (typeof value === 'string' && delimiter) {
      return value.split(delimiter)
    }
    return value
  }

  set(value: StorageItemValue): StorageItemValue {
    if (!value) {
      Cookies.remove(this.key)
      sessionStorage.removeItem(this.key)
      if (this.aliasKey) Cookies.remove(this.aliasKey)
    } else {
      const stringValue = typeof value === 'string' ? value : JSON.stringify(value)
      const options = this.expires ? { expires: this.expires } : {}
      Cookies.set(this.key, stringValue, options)
      sessionStorage.setItem(this.key, stringValue)
      if (this.aliasKey) Cookies.set(this.aliasKey, stringValue, options)
    }
    return value
  }
}

/**
 * Class for managing ID values with automatic generation
 */
class StorageID extends StorageValue {
  constructor(key: string, expires = 0, aliasKey?: string) {
    super(key, expires, aliasKey)
    this.get() // Initialize ID if not exists
  }

  get(): string {
    let id = super.get() as string | null
    if (!id) {
      id = super.set(this.generate()) as string
    }
    return id
  }

  /**
   * Force a fresh ID regardless of what's already stored. Used for
   * `regenerateOnLoad` keys (`pageLoadId`) so every navigation gets a new
   * value instead of reusing the one handed back by sessionStorage.
   */
  regenerate(): string {
    return super.set(this.generate()) as string
  }

  protected generate(): string {
    return generateID()
  }
}

/**
 * Main Session class for managing various IDs and values
 */
class Session {
  [key: string]: StorageItem | (() => void); // Allow dynamic properties while maintaining type safety

  /**
   * Athos first-party cookies. `aliasId` entries are legacy `ss*` cookie names
   * that must keep being written alongside the canonical `athos*` one — see
   * `StorageValue`'s aliasKey docs.
   */
  static readonly KEY_DEFINITIONS: SessionKeyDefinitions = {
    pageLoadId: { id: 'athosPageLoadId', expires: 0, generateIfEmpty: true, regenerateOnLoad: true, aliasId: 'ssPageLoadId' },
    shopper: { id: 'athosShopperId', expires: 365, generateIfEmpty: false },
    sessionId: { id: 'athosSessionId', expires: 0, generateIfEmpty: true, aliasId: 'ssSessionIdNamespace' },
    userId: { id: 'athosUserId', expires: 365, generateIfEmpty: true, aliasId: 'ssUserId' },
    viewedProducts: {
      id: 'athosViewedProducts',
      expires: 365 * 7,
      delimiter: ',',
      generateIfEmpty: false
    },
    columnPreferences: {
      id: 'athosColumnPreferences',
      expires: 0,
      generateIfEmpty: false
    }
  }

  pageLoadId!: StorageID
  shopper!: StorageValue
  sessionId!: StorageID
  userId!: StorageID
  viewedProducts!: StorageValue
  columnPreferences!: StorageValue

  constructor() {
    this.initializeStorageItems()
    this.setShopperID()

    if (typeof window !== 'undefined') {
      window.addEventListener('pageshow', (event: PageTransitionEvent) => {
        if (event.persisted) this.pageLoadId.regenerate()
      })
    }
  }

  protected initializeStorageItems(): void {
    for (const [key, value] of Object.entries(Session.KEY_DEFINITIONS)) {
      const StorageClass = value.generateIfEmpty ? StorageID : StorageValue
      const item = new StorageClass(value.id, value.expires, value.aliasId)
      this[key] = item
      if (value.generateIfEmpty) {
        // This will trigger ID generation if it doesn't exist.
        (item as StorageID).get()
        if (value.regenerateOnLoad) (item as StorageID).regenerate()
      }
    }
  }

  protected setShopperID(): void {
    const customerID = globalState?.customer?.id

    if (customerID) {
      this.shopper.set(customerID)
    } else {
      // delete shopper if customer is not logged in
      this.shopper.set(null)
    }
  }
}

// Export a singleton instance
export const session = new Session()
